/* eslint-disable id-length */
/* eslint-disable react/jsx-no-bind */
import { useBuild } from "app/components/build/Show/lib/BuildContext";
import { Job } from "app/components/build/Show/lib/types";
import * as TableCell from "./TableCell";
import { Link } from "react-router-dom";
import classNames from "classnames";
import { twMerge } from "tailwind-merge";
import { useBuildPreferencesStore } from "../../lib/useBuildPreferencesStore";
import Icon from "app/components/shared/Icon";
import { filterJobs, useFilterStore } from "../Sidebar/useFilterStore";
import { useMemo, useRef, useState, useCallback, useEffect } from "react";
import { resolveJobUrl } from "../../lib/urlForView";
import useQueryParams from "../../lib/useQueryParams";
import {
  useTableVirtualizer,
  VirtualizedTable,
  VirtualizedTableSpacer,
} from "app/components/shared/VirtualizedTable";
import { TableActionsDropdown } from "./TableActionsDropdown";

interface Column {
  isFlexible?: boolean;
  key: string;
  label: string | React.ReactNode;
  component: (props: { job: Job }) => React.ReactNode;
  sortFn?: TableCell.SortFn;
  defaultWidth: number;
  minWidth: number;
  maxWidth: number;
  isResizable?: boolean;
}

const columns: ReadonlyArray<Column> = [
  {
    key: "state",
    label: "State",
    component: TableCell.State,
    sortFn: TableCell.sortByState,
    defaultWidth: 70,
    minWidth: 70,
    maxWidth: 200,
    isResizable: true,
  },
  {
    key: "label",
    label: "Label",
    component: TableCell.Label,
    sortFn: TableCell.sortByLabel,
    defaultWidth: 180,
    minWidth: 110,
    maxWidth: 800,
    isFlexible: true,
  },
  {
    key: "command",
    label: "Command",
    component: TableCell.Command,
    sortFn: TableCell.sortByCommand,
    defaultWidth: 120,
    minWidth: 90,
    maxWidth: 600,
    isFlexible: true,
  },
  {
    key: "queue",
    label: "Queue",
    component: TableCell.Queue,
    sortFn: TableCell.sortByQueue,
    defaultWidth: 100,
    minWidth: 80,
    maxWidth: 400,
    isFlexible: true,
  },
  {
    key: "agent",
    label: "Agent",
    component: TableCell.Agent,
    sortFn: TableCell.sortByAgent,
    defaultWidth: 130,
    minWidth: 80,
    maxWidth: 500,
    isFlexible: true,
  },
  {
    key: "wait_duration",
    label: "Wait time",
    component: TableCell.WaitDuration,
    sortFn: TableCell.sortByWaitDuration,
    defaultWidth: 100,
    minWidth: 80,
    maxWidth: 250,
    isFlexible: true,
  },
  {
    key: "run_duration",
    label: "Run time",
    component: TableCell.RunDuration,
    sortFn: TableCell.sortByRunDuration,
    defaultWidth: 100,
    minWidth: 80,
    maxWidth: 250,
  },
  {
    key: "start_time",
    label: "Started at",
    component: TableCell.StartTime,
    sortFn: TableCell.sortByStartTime,
    defaultWidth: 170,
    minWidth: 100,
    maxWidth: 300,
    isFlexible: true,
  },
  {
    key: "actions",
    label: <TableActionsDropdown />,
    component: () => (
      <div className="flex justify-end w-full">
        <span />
      </div>
    ),
    defaultWidth: 40,
    minWidth: 40,
    maxWidth: 5000, // Arbitrary number to allow this column to act as a buffer
    isResizable: true,
  },
];

const useTableSorting = () => {
  const sortDirection = useBuildPreferencesStore((state) => state.sortDirection);
  const setSortDirection = useBuildPreferencesStore((state) => state.setSortDirection);

  const sortColumn = useBuildPreferencesStore((state) => state.sortColumn);
  const setSortColumn = useBuildPreferencesStore((state) => state.setSortColumn);

  const clickCountRef = useRef(0);

  const setSort = (column: string) => {
    if (column === sortColumn) {
      clickCountRef.current += 1;

      // Reset sorting if the same column is clicked twice
      if (clickCountRef.current > 1) {
        setSortColumn(null);
        setSortDirection(null);
        clickCountRef.current = 0;
      } else {
        setSortDirection(sortDirection === "asc" ? "desc" : "asc");
      }
    } else {
      clickCountRef.current = 0;
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  return { sortColumn, sortDirection, setSort };
};

const useColumnResizingInternal = (
  columnsConfig: ReadonlyArray<Column>,
  persistedWidths: Record<string, number>,
  setPersistedWidth: (key: string, width: number) => void,
) => {
  const [activeResize, setActiveResize] = useState<{
    key: string;
    startX: number; // Initial clientX from mousedown
    startWidth: number; // Initial column width from mousedown
    currentWidth: number; // Current width for rendering, updated by requestAnimationFrame
  } | null>(null);

  // Snapshot column widths before resize starts
  const [snapshotWidths, setSnapshotWidths] = useState<Record<string, number> | null>(null);
  const [resizingBoundary, setResizingBoundary] = useState<"min" | "max" | null>(null);

  const dragStateRef = useRef<{
    columnConfig: Column;
    animationFrameId: number | null;
    latestClientX: number;
  } | null>(null);

  const getColumnWidth = useCallback(
    (key: string): number => {
      if (activeResize && activeResize.key === key) {
        return activeResize.currentWidth;
      }

      // During resize operations, use snapshot widths to prevent flexible columns from redistributing
      if (activeResize && snapshotWidths && snapshotWidths[key] !== undefined) {
        return snapshotWidths[key];
      }

      const columnConfig = columnsConfig.find((c) => c.key === key);
      return persistedWidths[key] ?? columnConfig?.defaultWidth ?? 100;
    },
    [persistedWidths, columnsConfig, activeResize, snapshotWidths],
  );

  const handleMouseDown = useCallback(
    (key: string, event: React.MouseEvent<HTMLDivElement>) => {
      const columnConfig = columnsConfig.find((c) => c.key === key);
      if (!columnConfig || columnConfig.isResizable === false) {
        return;
      }

      // Note: preventDefault() is not called here to allow dblclick events to fire for resetting column width.
      // Text selection during drag is prevented by temporarily setting user-select: none on the body (in useEffect).
      const thElement = event.currentTarget.closest("th");
      if (!thElement) {
        console.error("TableView: Could not find parent <th> for resizer.");
        return;
      }
      // Snapshot all current column widths. This prevents other columns from reflowing during the drag.
      // Uses offsetWidth for a stable, integer-based reading of initial widths, avoiding float precision issues.
      const newSnapshot: Record<string, number> = {};
      const tableElement = thElement.closest("table");
      if (!tableElement) {
        console.error("TableView: Could not find parent <table> for resizer.");
        return;
      }

      columnsConfig.forEach((colConfig) => {
        const th = tableElement.querySelector(
          `th[data-column-key='${colConfig.key}']`,
        ) as HTMLElement | null;
        if (th) {
          newSnapshot[colConfig.key] = th.offsetWidth;
        } else {
          newSnapshot[colConfig.key] = persistedWidths[colConfig.key] ?? colConfig.defaultWidth;
        }
      });
      setSnapshotWidths(newSnapshot);

      const actualRenderedWidth = thElement.offsetWidth; // Get initial width of the column being dragged.

      setActiveResize({
        key,
        startX: event.clientX,
        startWidth: actualRenderedWidth,
        currentWidth: actualRenderedWidth,
      });
    },
    [columnsConfig, persistedWidths, setSnapshotWidths],
  );

  useEffect(() => {
    // This effect manages the global event listeners and document-level style changes (cursor, text selection)
    // for the duration of an active column resize drag operation.
    if (!activeResize) {
      // If not actively resizing, or if activeResize was cleared by another effect/event,
      // ensure no lingering drag state/listeners.
      if (dragStateRef.current?.animationFrameId) {
        cancelAnimationFrame(dragStateRef.current.animationFrameId);
      }
      dragStateRef.current = null;
      return;
    }

    // Initialize dragStateRef only if it's the start of a new drag session
    // (i.e., activeResize is set, but dragStateRef hasn't been initialized for this session yet)
    if (!dragStateRef.current) {
      const columnConfig = columnsConfig.find((c) => c.key === activeResize.key);
      if (!columnConfig) {
        setActiveResize(null);
        return; // Should not happen if activeResize.key is valid
      }
      dragStateRef.current = {
        columnConfig: columnConfig,
        animationFrameId: null,
        latestClientX: activeResize.startX, // Initialize latestClientX with startX from activeResize
      };
    }
    // If dragStateRef.current already exists, the drag is ongoing.
    // Its .latestClientX will be updated by handleMouseMove.
    // Its .initialClientX and .initialColumnWidth should remain from the true start of the drag.

    const handleMouseMove = (event: MouseEvent) => {
      if (!dragStateRef.current) {
        return;
      }

      dragStateRef.current.latestClientX = event.clientX;

      // Update width in a rAF callback to batch updates and sync with browser's paint cycle for smoothness.
      dragStateRef.current.animationFrameId = requestAnimationFrame(() => {
        if (!dragStateRef.current || !activeResize) {
          if (dragStateRef.current) {
            dragStateRef.current.animationFrameId = null;
          }
          return;
        }

        // Use startX and startWidth directly from activeResize state (which holds original mousedown values)
        const { startX, startWidth } = activeResize;
        const { columnConfig: currentColumnConfig, latestClientX } = dragStateRef.current;

        const deltaX = latestClientX - startX;
        let newWidth = startWidth + deltaX;

        newWidth = Math.max(currentColumnConfig.minWidth, newWidth);
        newWidth = Math.min(currentColumnConfig.maxWidth, newWidth);

        if (newWidth === currentColumnConfig.minWidth) {
          setResizingBoundary("min");
        } else if (newWidth === currentColumnConfig.maxWidth) {
          setResizingBoundary("max");
        } else {
          setResizingBoundary(null);
        }

        if (activeResize.key === currentColumnConfig.key) {
          setActiveResize((prev) => (prev ? { ...prev, currentWidth: newWidth } : null));
        }

        if (dragStateRef.current) {
          // Check if ref is still valid after async operation
          dragStateRef.current.animationFrameId = null;
        }
      });
    };

    const handleMouseUp = (event: MouseEvent) => {
      if (dragStateRef.current?.animationFrameId) {
        cancelAnimationFrame(dragStateRef.current.animationFrameId);
        // No need to nullify animationFrameId here, full ref cleanup will happen or next drag will init
      }

      if (activeResize && dragStateRef.current) {
        const { key, startX, startWidth, currentWidth } = activeResize; // Destructure all needed properties
        const { columnConfig: currentColumnConfig } = dragStateRef.current;

        // Only proceed if an actual drag occurred (mouse moved significantly or width changed)
        // A small threshold (e.g., 2 pixels) can account for minor mouse jitter on a click.
        // event.clientX is available on the mouseup event.
        const hasDragged = Math.abs(event.clientX - startX) > 2 || currentWidth !== startWidth;

        if (hasDragged) {
          // Use the last width calculated by rAF (activeResize.currentWidth) for the final persisted width.
          // This ensures the column settles exactly where the user saw the drag indicator.
          // Note: currentWidth is already clamped during the drag, so no need to clamp again
          const finalWidth = currentWidth;
          const widthDelta = finalWidth - startWidth; // How much the column changed

          setPersistedWidth(key, finalWidth);

          // Actions column buffer: compensate for the width change to maintain table width
          const actionsColumn = columnsConfig.find((col) => col.key === "actions");

          if (actionsColumn && snapshotWidths && widthDelta !== 0) {
            const actionsCurrentWidth =
              snapshotWidths[actionsColumn.key] ?? actionsColumn.defaultWidth;
            let actionsNewWidth = actionsCurrentWidth - widthDelta;

            // Clamp to constraints
            actionsNewWidth = Math.max(actionsColumn.minWidth, actionsNewWidth);
            actionsNewWidth = Math.min(actionsColumn.maxWidth, actionsNewWidth);

            setPersistedWidth(actionsColumn.key, actionsNewWidth);
          }
        }
      }

      // If no resize occurred, clear the activeResize state and snapshot
      setActiveResize(null);
      setSnapshotWidths(null);
      setResizingBoundary(null); // Reset boundary state on mouse up
    };

    document.body.style.userSelect = "none"; // Prevent text selection issues during drag.
    document.documentElement.addEventListener("mousemove", handleMouseMove);
    document.documentElement.addEventListener("mouseup", handleMouseUp);
    document.documentElement.style.setProperty("cursor", "col-resize", "important");

    const styleElement = document.createElement("style");
    styleElement.textContent = "* { cursor: col-resize !important; }";
    document.head.appendChild(styleElement);

    return () => {
      document.body.style.userSelect = ""; // Restore default text selection.
      document.documentElement.removeEventListener("mousemove", handleMouseMove);
      document.documentElement.removeEventListener("mouseup", handleMouseUp);
      document.documentElement.style.removeProperty("cursor");

      if (styleElement.parentNode) {
        styleElement.parentNode.removeChild(styleElement);
      }

      if (dragStateRef.current?.animationFrameId) {
        cancelAnimationFrame(dragStateRef.current.animationFrameId);
      }
      dragStateRef.current = null;
    };
  }, [activeResize, columnsConfig, setPersistedWidth, setSnapshotWidths]);

  return {
    activeResize,
    snapshotWidths,
    getColumnWidth,
    handleMouseDown,
    resizingColumnKey: activeResize?.key,
    resizingBoundary, // Expose the boundary state
  };
};

const useColumnResizing = useColumnResizingInternal;

/**
 * TableView component with column resizing functionality.
 *
 * The resize behavior uses the actions column as a buffer:
 * - When any column is resized, the actions column adjusts its width to compensate
 * - All other columns remain stable and predictable, which prevents unexpected jumping
 * - The table will always use the full width available to it
 * - During resize operations, all columns use explicit pixel widths
 */
export function TableView() {
  const { build } = useBuild();

  const { sortColumn, sortDirection, setSort } = useTableSorting();
  const filter = useFilterStore((state) => state.filterBy);
  const params = useQueryParams();

  const columnWidths = useBuildPreferencesStore((state) => state.columnWidths);
  const setColumnWidth = useBuildPreferencesStore((state) => state.setColumnWidth);

  const [hoveredResizerKey, setHoveredResizerKey] = useState<string | null>(null);

  const {
    activeResize,
    snapshotWidths,
    handleMouseDown, // Destructure directly, JSX expects this name
    resizingColumnKey,
    resizingBoundary, // Add resizingBoundary here
  } = useColumnResizing(columns, columnWidths, setColumnWidth);

  const handleDoubleClickResizer = useCallback(
    (columnKey: string) => {
      const columnConfig = columns.find((c) => c.key === columnKey);
      if (columnConfig) {
        setColumnWidth(columnKey, columnConfig.defaultWidth);
      }
    },
    [setColumnWidth],
  );

  const jobs = useMemo(() => {
    // Filter to executable jobs
    let jobs =
      build?.jobs.filter(
        (job) => job.state !== "broken" && (job.type === "script" || job.type === "trigger"),
      ) || [];

    // Filter jobs based on the current filter
    jobs = filterJobs(jobs, filter, build);

    // Sort jobs by the current sort column/direction
    const column = columns.find((c) => c.key === sortColumn);
    if (column?.sortFn && sortDirection) {
      jobs.sort(column.sortFn(sortDirection));
    }

    return jobs;
  }, [build, filter, sortColumn, sortDirection]);

  const parentRef = useRef(null);

  const { virtualizer, virtualItems, heightBeforeItems, heightAfterItems } = useTableVirtualizer({
    items: jobs,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 38, // Row height
    overscan: 20,
  });

  const totalMinimumTableWidth = useMemo(
    () => columns.reduce((sum, col) => sum + col.minWidth, 0),
    [],
  );

  // Calculate the effective table width during resize to ensure consistent behavior
  const effectiveTableWidth = useMemo(() => {
    if (activeResize) {
      // During resize, calculate explicit width based on current column widths
      return columns.reduce((sum, col) => {
        if (col.key === activeResize.key) {
          return sum + activeResize.currentWidth;
        }
        return sum + (snapshotWidths?.[col.key] ?? columnWidths[col.key] ?? col.defaultWidth);
      }, 0);
    }
    return totalMinimumTableWidth;
  }, [activeResize, snapshotWidths, columnWidths, columns, totalMinimumTableWidth]);

  return (
    <VirtualizedTable ref={parentRef} virtualizer={virtualizer} data-testid="build-table">
      <table
        className="border-separate table-fixed"
        style={{
          width: activeResize ? effectiveTableWidth : "100%",
          minWidth: totalMinimumTableWidth,
        }}
      >
        <thead className="sticky top-0 z-10">
          <tr>
            {columns.map((column, columnIndex) => {
              const isLastColumn = columnIndex === columns.length - 1;
              return (
                <th
                  key={column.key}
                  data-column-key={column.key}
                  style={{
                    width: (() => {
                      // Always use explicit widths during resize to prevent browser redistribution
                      if (activeResize) {
                        return activeResize.key === column.key
                          ? activeResize.currentWidth
                          : snapshotWidths?.[column.key] ??
                              columnWidths[column.key] ??
                              column.defaultWidth;
                      }

                      // When not resizing, allow flexible columns to use auto only if they haven't been explicitly sized
                      return column.isFlexible && !columnWidths[column.key]
                        ? "auto"
                        : columnWidths[column.key] ?? column.defaultWidth;
                    })(),
                    minWidth: column.minWidth,
                    maxWidth: column.maxWidth,
                  }}
                  // Visual feedback for the column header when being resized
                  className={classNames(
                    "relative px-2.5 py-2 first:pl-4 last:pr-1 bg-white backdrop-blur border-b border-b-gray-400 select-none",
                    {
                      "hover:underline cursor-pointer": column.sortFn,
                      underline: column.key === sortColumn,
                      "border-r border-gray-300 bg-white": !isLastColumn,
                    },
                  )}
                >
                  <div
                    onClick={column.sortFn ? () => setSort(column.key) : undefined}
                    className="flex-grow h-full"
                    style={{
                      cursor: column.sortFn ? "pointer" : "default",
                      maxWidth: column.maxWidth, // Content wrapper respects maxWidth
                      overflow: "hidden",
                    }}
                  >
                    <span className="flex items-center font-medium">
                      {typeof column.label === "string" ? (
                        <span className="truncate max-w-full">{column.label}</span>
                      ) : (
                        column.label
                      )}
                      {column.key === sortColumn && (
                        <Icon
                          icon={
                            sortDirection === "desc"
                              ? "heroicons/outline/arrow-down"
                              : "heroicons/outline/arrow-up"
                          }
                          className="w-2 h-2 ml-1 shrink-0"
                        />
                      )}
                    </span>
                  </div>
                  {/* Resizer Handle Area - rendered for all columns except 'actions' */}
                  {column.key !== "actions" && (
                    <div
                      onMouseDown={
                        column.isResizable !== false
                          ? (e) => handleMouseDown(column.key, e)
                          : undefined
                      }
                      onMouseEnter={() => setHoveredResizerKey(column.key)} // Always set hover key to show feedback
                      onMouseLeave={() => setHoveredResizerKey(null)}
                      onDoubleClick={
                        column.isResizable !== false
                          ? (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleDoubleClickResizer(column.key);
                            }
                          : undefined
                      }
                      role="separator"
                      aria-orientation="vertical"
                      title={
                        column.isResizable !== false
                          ? "Drag to resize. Double-click to reset width."
                          : "This column is not resizable."
                      }
                      className={classNames(
                        "absolute top-0 h-full w-4 select-none touch-none z-20",
                        column.isResizable !== false ? "cursor-col-resize" : "pointer",
                        isLastColumn ? "right-0" : "right-[-8px]",
                      )}
                    >
                      <div // Visual resizer line
                        className={(() => {
                          const isColResizable = column.isResizable !== false;
                          const isColHovered = hoveredResizerKey === column.key;
                          const isColResizing = resizingColumnKey === column.key;
                          let bgColorClass = "bg-transparent"; // Default

                          if (isColResizable) {
                            if (isColResizing) {
                              // Prioritize resizing state
                              if (resizingBoundary) {
                                // Check if boundary is hit ('min' or 'max')
                                bgColorClass = "bg-slate-400"; // Boundary hit color
                              } else {
                                bgColorClass = "bg-purple-600"; // Normal resize color
                              }
                            } else if (isColHovered) {
                              bgColorClass = "bg-purple-600"; // Hover color
                            }
                          } else {
                            // Not resizable
                            if (isColHovered) {
                              bgColorClass = "bg-gray-300";
                            }
                          }
                          return classNames(
                            "absolute top-0 h-full w-[2px] left-[7px] pointer-events-none",
                            bgColorClass,
                          );
                        })()}
                      />
                    </div>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          <VirtualizedTableSpacer height={heightBeforeItems} colSpan={columns.length} />
          {virtualItems.map((virtualItem) => {
            const job = jobs[virtualItem.index];
            return (
              <Link
                key={job.id}
                id={`job-${job.id}`}
                to={resolveJobUrl(job.id)}
                data-testid="jobs-table-row"
                className={twMerge(
                  "select-text text-inherit table-row",
                  "hover:text-inherit hover:no-underline hover:bg-gray-100",
                  "focus:text-inherit focus:no-underline",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-opacity-50",
                  classNames({
                    "bg-gray-100": params.jobUuid === job.id || params.stepUuid === job.stepUuid,
                    "text-charcoal-300 hover:text-charcoal-300":
                      job.type === "script" && job.retriedInJobUuid,
                  }),
                )}
              >
                {columns.map((column, columnIndex) => (
                  <td
                    key={column.key}
                    style={{
                      width: (() => {
                        // Always use explicit widths during resize to prevent browser redistribution
                        if (activeResize) {
                          return activeResize.key === column.key
                            ? activeResize.currentWidth // Already clamped by rAF
                            : snapshotWidths && snapshotWidths[column.key] !== undefined
                              ? Math.max(column.minWidth, snapshotWidths[column.key]) // Clamp snapshot for non-dragged cols
                              : columnWidths[column.key] ?? column.defaultWidth;
                        }

                        // When not resizing, allow flexible columns to use auto only if they haven't been explicitly sized
                        return column.isFlexible && !columnWidths[column.key]
                          ? "auto"
                          : columnWidths[column.key] ?? column.defaultWidth;
                      })(),
                      minWidth: column.minWidth,
                      maxWidth: column.maxWidth,
                    }}
                    className={twMerge(
                      "py-2 px-2.5 first:pl-4 last:pr-1 align-middle border-b border-b-gray-400 overflow-hidden",
                      (() => {
                        if (columnIndex < columns.length - 1 && column.isResizable !== false) {
                          const isColResizing = column.key === resizingColumnKey;
                          const isColHovered = column.key === hoveredResizerKey;

                          if (isColResizing) {
                            if (resizingBoundary) {
                              // 'min' or 'max'
                              return "border-r-2 border-r-slate-400 border-b-gray-400";
                            }
                            return "border-r-2 border-r-purple-600 border-b-gray-400";
                          } else if (isColHovered) {
                            return "border-r-2 border-r-purple-600 border-b-gray-400";
                          }
                        }
                        return null; // No special border class if conditions not met
                      })(),
                    )}
                  >
                    <div
                      className="truncate"
                      style={{ maxWidth: column.maxWidth, overflow: "hidden" }}
                    >
                      {column.component({ job }) || <span className="text-gray-800">—</span>}
                    </div>
                  </td>
                ))}
              </Link>
            );
          })}
          <VirtualizedTableSpacer height={heightAfterItems} colSpan={columns.length} />
        </tbody>
      </table>
    </VirtualizedTable>
  );
}
